/**
 * DD-BE-PR2b-FILES: design-file + proof API for D&D overlay zones.
 * Gated by products_design.queue.view.
 */
import type { AxiosRequestConfig } from 'axios'
import apiService from '@/services/api'
import {
  narrowPiqQuoteStatus,
  narrowPiqUnitPriceSource,
  type PiqQuoteStatus,
  type PiqUnitPriceSource,
} from '@/lib/dd-queue/types/subjectMutation'

const STRICT_SUCCESS_HTTP: AxiosRequestConfig = {
  validateStatus: (status) => status >= 200 && status < 300,
}

export type DesignDevelopmentQueueFileKind = 'design_file' | 'proof' | 'quote'

export type DesignDevelopmentQueueFileApi = {
  uuid: string
  line_key: string
  kind: DesignDevelopmentQueueFileKind
  category: string | null
  proof_version: number | null
  parent_uuid: string | null
  sort_order: number | null
  packet_snapshot: Record<string, unknown> | null
  original_name: string
  mime: string | null
  size: number
  uploaded_by_name: string | null
  source?: string | null
  uploaded_at: string | null
  download_url: string
  /** Main proofs only — GET lazy-generates when missing. */
  thumb_download_url?: string | null
}

interface ListEnvelope {
  success: boolean
  data: DesignDevelopmentQueueFileApi[]
}

interface OneEnvelope {
  success: boolean
  data: DesignDevelopmentQueueFileApi
  piq_parse?: {
    outcome?: string | null
    ok?: boolean | null
    message?: string | null
    quote_number?: string | null
    unit_price_cents?: number | null
    piq_quote_status?: string | null
    piq_unit_price_source?: string | null
  } | null
}

export type DesignDevelopmentQueueFileUploadResult = DesignDevelopmentQueueFileApi & {
  piqParse?: {
    outcome?: string | null
    ok?: boolean | null
    message?: string | null
    quoteNumber?: string | null
    unitPriceCents?: number | null
    piqQuoteStatus?: PiqQuoteStatus | null
    piqUnitPriceSource?: PiqUnitPriceSource | null
  } | null
}

function filesBase(submissionUuid: string): string {
  return `/v1/design-development/submissions/${submissionUuid}/files`
}

export async function listDesignDevelopmentQueueFiles(
  submissionUuid: string,
  lineKey?: string
): Promise<DesignDevelopmentQueueFileApi[]> {
  const response = await apiService.get<ListEnvelope>(filesBase(submissionUuid), {
    params: lineKey ? { line_key: lineKey } : undefined,
    ...STRICT_SUCCESS_HTTP,
  })
  if (!response?.success || !Array.isArray(response.data)) {
    throw new Error('Failed to load design-development files')
  }
  return response.data
}

export async function uploadDesignDevelopmentQueueFile(args: {
  submissionUuid: string
  file: File
  lineKey: string
  kind: DesignDevelopmentQueueFileKind
  category?: string
  parentUuid?: string
  onProgress?: (pct: number) => void
}): Promise<DesignDevelopmentQueueFileUploadResult> {
  const body = new FormData()
  body.append('file', args.file)
  body.append('line_key', args.lineKey)
  body.append('kind', args.kind)
  if (args.kind === 'design_file' && args.category) {
    body.append('category', args.category)
  }
  if (args.parentUuid) {
    body.append('parent_uuid', args.parentUuid)
  }

  const response = await apiService.post<OneEnvelope>(filesBase(args.submissionUuid), body, {
    ...STRICT_SUCCESS_HTTP,
    onUploadProgress: (event) => {
      if (!args.onProgress || !event.total) return
      args.onProgress(Math.round((event.loaded / event.total) * 100))
    },
  })
  if (!response?.success || !response.data) {
    throw new Error('Failed to upload file')
  }
  const piq = response.piq_parse
  return {
    ...response.data,
    piqParse: piq
      ? {
          outcome: piq.outcome ?? null,
          ok: piq.ok ?? null,
          message: piq.message ?? null,
          quoteNumber: piq.quote_number ?? null,
          unitPriceCents: piq.unit_price_cents ?? null,
          piqQuoteStatus: narrowPiqQuoteStatus(piq.piq_quote_status),
          piqUnitPriceSource: narrowPiqUnitPriceSource(piq.piq_unit_price_source),
        }
      : null,
  }
}

export async function reorderDesignDevelopmentSupportingFiles(args: {
  submissionUuid: string
  parentUuid: string
  orderedUuids: string[]
}): Promise<DesignDevelopmentQueueFileApi[]> {
  const response = await apiService.patch<ListEnvelope>(
    `${filesBase(args.submissionUuid)}/${args.parentUuid}/supporting-order`,
    { ordered_uuids: args.orderedUuids },
    STRICT_SUCCESS_HTTP
  )
  if (!response?.success || !Array.isArray(response.data)) {
    throw new Error('Failed to reorder supporting files')
  }
  return response.data
}

export async function deleteDesignDevelopmentQueueFile(
  submissionUuid: string,
  fileUuid: string
): Promise<void> {
  await apiService.delete(`${filesBase(submissionUuid)}/${fileUuid}`, STRICT_SUCCESS_HTTP)
}

/** Auth-bearing download — bare <a href> to download_url omits Bearer and gets 401. */
export async function downloadDesignDevelopmentQueueFileBlob(
  submissionUuid: string,
  fileUuid: string
): Promise<Blob> {
  const response = await apiService.getWithResponse<Blob>(
    `${filesBase(submissionUuid)}/${fileUuid}/download`,
    {
      responseType: 'blob',
      ...STRICT_SUCCESS_HTTP,
    }
  )
  return response.data
}

/** DD-PROOF-THUMB-001 — derived thumb bytes (lazy-generated server-side). */
export async function downloadDesignDevelopmentQueueFileThumbnailBlob(
  submissionUuid: string,
  fileUuid: string
): Promise<Blob> {
  const response = await apiService.getWithResponse<Blob>(
    `${filesBase(submissionUuid)}/${fileUuid}/thumbnail`,
    {
      responseType: 'blob',
      ...STRICT_SUCCESS_HTTP,
    }
  )
  return response.data
}

/**
 * DD-FILE-OPEN-001 (F-072) — blob delivery to the user.
 *
 * Auth-blob files (bearer token, no public URL) reach the browser as a Blob after an
 * awaited fetch. `window.open` AFTER that await runs outside the click's user
 * activation: Safari blocks it always, Chrome/Firefox once the fetch took longer
 * than the activation window (~5 s — any real reference PDF). The old helper did
 * exactly that, with `noopener` (window.open then always returns null), so a blocked
 * pop-up was indistinguishable from success: "Reference PDFs will not download".
 *
 * Rules now:
 * - Anything that is not a viewer-friendly type (PDF, raster image) is DOWNLOADED with
 *   its original filename via an anchor — downloads need no pop-up, and inline SVG /
 *   HTML in our origin was never wanted (the API refuses inline SVG for the same reason).
 * - Viewer types open in a new tab; the blob is our own content, so the tab is opened
 *   WITHOUT `noopener` (return value meaningful) and the opener is severed by hand. A
 *   blocked pop-up falls back to the same download and reports `popup_blocked` so the
 *   caller can tell the user — never silent.
 */
const VIEWABLE_MIME = /^(application\/pdf|image\/(png|jpe?g|gif|webp|bmp|avif))$/i

/** True when the browser can show this blob in a tab (PDF viewer / raster image). */
export function isViewableInTab(mime: string | null | undefined): boolean {
  return VIEWABLE_MIME.test((mime ?? '').split(';')[0]!.trim())
}

/** `popup_blocked` = the browser refused the tab, so the file was downloaded instead. */
export type BlobDeliveryMode = 'opened' | 'downloaded' | 'popup_blocked'

function revokeLater(url: string): void {
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/** Download a blob under `filename` via a transient anchor (no pop-up involved). */
export function saveBlobAs(blob: Blob, filename: string): string {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'download'
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
  revokeLater(url)
  return url
}

/**
 * Open a viewer-friendly blob in a new tab; download it instead when the browser blocks
 * the pop-up or the type is not viewable. Returns how the file reached the user.
 */
export function openBlobInNewTab(blob: Blob, filename = 'download'): BlobDeliveryMode {
  if (!isViewableInTab(blob.type)) {
    saveBlobAs(blob, filename)
    return 'downloaded'
  }
  const url = URL.createObjectURL(blob)
  const win = window.open(url, '_blank')
  if (!win) {
    URL.revokeObjectURL(url)
    saveBlobAs(blob, filename)
    return 'popup_blocked'
  }
  try {
    win.opener = null
  } catch {
    /* cross-process window — opener already inaccessible */
  }
  revokeLater(url)
  return 'opened'
}

/** One user-facing line for the pop-up fallback; null when nothing needs saying. */
// ---------------------------------------------------------------------------
// DD-WEB-IMAGES-ZIP-001 (F-043 part 2 / F-097) — Web Images as one zip, named for PIM / Web
// (`SKU_web_01.png`; the line id until a SKU is assigned). The server names the entries.
// ---------------------------------------------------------------------------

export interface DdWebImagesZipResult {
  blob: Blob
  filename: string
  /** Files in the archive. */
  files: number
  /** Lines that contributed at least one file. */
  lines: number
  /** Lines in the selection that have no Web Images. */
  linesEmpty: number
}

/** A zip refusal the user should read as-is (nothing there / over the size cap). */
export class DdWebImagesZipError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message)
    this.name = 'DdWebImagesZipError'
  }
}

export const NO_WEB_IMAGES_MESSAGE = 'No web images on the selected lines.'

const ZIP_HTTP: AxiosRequestConfig = {
  responseType: 'blob',
  // 404 (nothing there) and 422 (over the cap) carry a message we show verbatim.
  validateStatus: (status) => (status >= 200 && status < 300) || status === 404 || status === 422,
}

export async function downloadWebImagesZip(
  submissionUuid: string,
  lineKey?: string | null
): Promise<DdWebImagesZipResult> {
  const params: Record<string, string> = { category: 'web_image' }
  if (lineKey) params.line_key = lineKey
  const response = await apiService.getWithResponse<Blob>(`${filesBase(submissionUuid)}/zip`, {
    ...ZIP_HTTP,
    params,
  })
  return await zipResult(response.status, response.headers as Record<string, unknown>, response.data)
}

export async function downloadWebImagesZipForLines(
  lines: Array<{ submission_uuid: string; line_key: string }>
): Promise<DdWebImagesZipResult> {
  // 404 / 422 carry the message we show; keep them out of the generic client-error rejection.
  const response = await apiService.postWithResponse<Blob>(
    '/v1/design-development/files/zip',
    { category: 'web_image', lines },
    ZIP_HTTP,
    { rejectClientErrors: false }
  )
  return await zipResult(response.status, response.headers as Record<string, unknown>, response.data)
}

async function zipResult(
  status: number,
  headers: Record<string, unknown>,
  data: Blob
): Promise<DdWebImagesZipResult> {
  if (status === 404 || status === 422) {
    throw new DdWebImagesZipError(await blobErrorMessage(data, status), status)
  }
  const header = (name: string): string => {
    const v = headers[name] ?? headers[name.toLowerCase()]
    return typeof v === 'string' ? v : ''
  }
  return {
    blob: data,
    filename: filenameFromDisposition(header('content-disposition'), 'web-images.zip'),
    files: Number.parseInt(header('x-dd-zip-files'), 10) || 0,
    lines: Number.parseInt(header('x-dd-zip-lines'), 10) || 0,
    linesEmpty: Number.parseInt(header('x-dd-zip-lines-empty'), 10) || 0,
  }
}

/** The JSON `message` of an error body that arrived as a blob (responseType: 'blob'). */
async function blobErrorMessage(data: Blob | undefined, status: number): Promise<string> {
  const fallback = status === 404 ? NO_WEB_IMAGES_MESSAGE : `Request failed with status ${status}`
  if (!data || typeof data.text !== 'function') return fallback
  try {
    const parsed = JSON.parse(await data.text()) as { message?: unknown }
    return typeof parsed.message === 'string' && parsed.message.trim() ? parsed.message : fallback
  } catch {
    return fallback
  }
}

/** `attachment; filename="x.zip"` / `filename*=UTF-8''x.zip` → x.zip; the fallback otherwise. */
export function filenameFromDisposition(header: string, fallback: string): string {
  const star = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(header)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim())
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/.exec(header)
  return plain?.[1]?.trim() || fallback
}

export function blobDeliveryNotice(mode: BlobDeliveryMode, filename: string): string | null {
  return mode === 'popup_blocked'
    ? `Your browser blocked the new tab — "${filename}" was downloaded instead.`
    : null
}

